/*
  Bag drawer extras.
  1. The first-order voucher button. It asks rosie-credit.js for the state, so the
     amount always matches the shopper's market and the popup is exactly the same one as before.
  2. Apple Pay / Google Pay: shows the "or" divider only when Shopify actually drew a wallet button, and
     re-fetches the buttons if the drawer was first rendered with an empty cart.
*/
(() => {
  'use strict';
  if (window.__rosieDrawerExtras) return;
  window.__rosieDrawerExtras = true;

  const bagOpen = () => document.querySelector('.hbr-cart-drawer.is-open');

  /* ---------- 1. welcome credit button ---------- */
  function creditInfo() {
    const credit = window.RosieCredit;
    const market = credit?.market?.();
    if (!credit || !market) return null;
    const claimed = Boolean(credit.claimed());
    const canSignup = typeof credit.canSignup === 'function' ? credit.canSignup() : true;
    if (!claimed && !canSignup) return null; // never advertise a signup that can't complete
    return { credit, claimed, amount: credit.format(market.welcome) };
  }

  function paintCredit() {
    const info = creditInfo();
    document.querySelectorAll('[data-rosie-drawer-credit]').forEach(button => {
      if (!info || !info.amount) { button.hidden = true; return; }
      const text = button.querySelector('[data-rdc-text]');
      const action = button.querySelector('[data-rdc-action]');
      button.dataset.state = info.claimed ? 'claimed' : 'open';
      if (text && button.hasAttribute('data-rdc-compact')) {
        text.textContent = (info.claimed ? 'View ' : 'Claim ') + info.amount;
      } else if (text) {
        const strong = document.createElement('strong');
        strong.textContent = info.amount;
        text.replaceChildren(...(info.claimed
          ? [document.createTextNode('Your '), strong, document.createTextNode(' welcome voucher')]
          : [strong, document.createTextNode(' off your first order')]));
      }
      if (action) action.textContent = info.claimed ? 'View' : 'Claim';
      button.setAttribute('aria-label', info.claimed
        ? 'View your ' + info.amount + ' welcome voucher'
        : 'Claim ' + info.amount + ' off your first order');
      button.hidden = false;
    });
  }

  document.addEventListener('click', event => {
    const button = event.target instanceof Element && event.target.closest('[data-rosie-drawer-credit]');
    if (!button) return;
    event.preventDefault();
    const info = creditInfo();
    if (!info) { button.hidden = true; return; }
    info.credit.open(info.claimed ? 'balance' : 'signup');
  });

  // After the popup closes (e.g. they just claimed), refresh the bag so the discount shows in the subtotal.
  document.addEventListener('close', event => {
    if (!(event.target instanceof Element) || !event.target.closest('#rosie-welcome-credit')) return;
    paintCredit();
    if (bagOpen()) setTimeout(() => window.HbrSharedBag?.refresh?.(), 250);
  }, true);

  /* ---------- 2. Apple Pay / Google Pay ---------- */
  function watchExpress(box) {
    if (box.__rosieWatched) return;
    box.__rosieWatched = true;
    const buttons = box.querySelector('[data-rosie-express-buttons]');
    if (!buttons) return;
    const check = () => box.toggleAttribute('data-has-buttons', buttons.getBoundingClientRect().height > 12);
    if ('ResizeObserver' in window) new ResizeObserver(check).observe(buttons);
    check();
  }

  async function refetchExpress(buttons) {
    if (buttons.children.length || buttons.dataset.loading) return;
    if (Date.now() - (Number(buttons.dataset.tried) || 0) < 30000) return;
    buttons.dataset.tried = String(Date.now());
    buttons.dataset.loading = '1';
    try {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const response = await fetch(root + '?section_id=rosie-express-checkout', { credentials: 'same-origin' });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = doc.querySelector('[id^="shopify-section"]') || doc.body;
      if (!section || !section.children.length || buttons.children.length) return;
      buttons.replaceChildren(...[...section.childNodes].map(node => document.importNode(node, true)));
      // Only if Shopify's wallet script isn't on the page yet: let its scripts run once.
      if (!customElements.get('shopify-accelerated-checkout-cart')) {
        buttons.querySelectorAll('script').forEach(old => {
          const script = document.createElement('script');
          [...old.attributes].forEach(attr => script.setAttribute(attr.name, attr.value));
          script.textContent = old.textContent;
          old.replaceWith(script);
        });
      }
    } catch { /* the normal checkout button still works */ }
    finally { delete buttons.dataset.loading; }
  }

  function setupExpress() {
    document.querySelectorAll('[data-rosie-express]').forEach(watchExpress);
    const drawer = bagOpen();
    if (!drawer || !drawer.querySelector('.hbr-cart-drawer__item')) return;
    drawer.querySelectorAll('[data-rosie-express-buttons]').forEach(refetchExpress);
  }

  /* ---------- wiring ---------- */
  const paintAll = () => { paintCredit(); setupExpress(); };
  document.addEventListener('rosie:welcome:state', paintCredit);
  document.addEventListener('rosie:bag:open', () => {
    paintAll();
    setTimeout(setupExpress, 900); // after the drawer has loaded the cart items
  });
  document.addEventListener('shopify:section:load', () => setTimeout(paintAll, 0));
  window.addEventListener('pageshow', paintAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintAll, { once: true });
  else paintAll();
  // rosie-credit.js may finish after us; paint again once it has.
  window.addEventListener('load', paintAll, { once: true });
})();
